import { UnitSize } from '@/constants/measured-scope'
import { useUI } from '@/context/ui'
import classNames from 'classnames/bind'
import { useRouter } from 'next/router'
import React, { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as styles from './style'
const cx = classNames.bind(styles)
let _touchX: number = 0
let _activeSlide: number = 0

export interface Props {
  items: ReactNode[]
  dragThreshold?: number
  spacing?: UnitSize
  className?: string
  slideClassName?: string
  onChange?: (index: number) => void
  // margin + padding hack for the wrapper to allow box shadow slides without cutting them off
  shadowBleed?: string
  trackClasses?: string
  fill?: boolean
}

export interface SliderInterface {
  next: () => void
  prev: () => void
  setActiveSlide: (index: number) => void
  getActiveSlide: () => number | undefined
}

interface UseSliderRef extends SliderInterface {
  ref: React.RefObject<SliderInterface>
}

export const useSliderRef = (): UseSliderRef => {
  const sliderRef = React.useRef<SliderInterface>(null)
  return {
    ref: sliderRef,
    next: (): void => {
      sliderRef.current?.next()
    },
    prev: (): void => {
      sliderRef.current?.prev()
    },
    setActiveSlide: (index: number): void => {
      sliderRef.current?.setActiveSlide(index)
    },
    getActiveSlide: (): number | undefined => {
      return sliderRef.current?.getActiveSlide()
    },
  }
}

const Slider: React.ForwardRefRenderFunction<SliderInterface, Props> = (
  {
    items,
    dragThreshold = 280,
    onChange,
    className = '',
    slideClassName = '',
    shadowBleed,
    trackClasses = '',
    fill = false,
  },
  ref,
) => {
  const classes = cx({
    root: true,
    [className]: true,
  })

  const activeRef = useRef<HTMLDivElement>(null)
  const { state } = useUI()
  const route = useRouter()
  const trackRef = useRef<HTMLDivElement>(null)
  const [activeSlide, setActiveSlide] = useState(_activeSlide)
  const [trackX, setTrackX] = useState(0)
  const [dragLeft, setDragLeft] = useState(0)
  const [offsetCompensation, setOffsetCompensation] = useState(0)

  useEffect(() => {
    // TODO: Change this to schedule/browse
    if (route?.asPath.includes('/schedule/browse') && typeof state.schedulePage.index === 'number') {
      setActiveSlide(state.schedulePage.index)
    }
  }, [state])

  const handlePrev = useCallback((): void => {
    if (items && _activeSlide === 0) setActiveSlide(items.length - 1)
    else setActiveSlide(_activeSlide - 1)
  }, [items])
  const handleNext = useCallback((): void => {
    if (items && _activeSlide === items.length - 1) setActiveSlide(0)
    else setActiveSlide(_activeSlide + 1)
  }, [items])
  const handleTouchStart = function ({ touches }: TouchEvent): void {
    _touchX = touches[0].clientX
    trackRef.current?.classList.add('isMoving')
  }
  const handleTouchMove = function ({ touches }: TouchEvent): void {
    const diff = _touchX - touches[0].clientX
    setDragLeft(diff)
  }
  const handleTouchEnd = function (event: TouchEvent): void {
    const endX = event?.changedTouches[0].clientX
    const diff = endX - _touchX

    if (diff >= dragThreshold) {
      handlePrev()
    }
    if (diff <= -dragThreshold) {
      handleNext()
    }
    trackRef.current?.classList.remove('isMoving')
    _touchX = 0
  }

  const getActiveSlide = (): number => activeSlide

  const styleOverride = shadowBleed ? ({ '--shadow-bleed': shadowBleed } as React.CSSProperties) : {}

  useImperativeHandle(
    ref,
    () => ({
      next: handleNext,
      prev: handlePrev,
      setActiveSlide,
      getActiveSlide,
    }),
    [items],
  )

  useEffect(() => {
    if (activeRef.current) {
      setOffsetCompensation(activeRef.current.offsetLeft)
    }

    if (trackRef.current) {
      trackRef.current.addEventListener('touchstart', handleTouchStart, false)
      trackRef.current.addEventListener('touchmove', handleTouchMove, false)
      trackRef.current.addEventListener('touchend', handleTouchEnd, false)
      trackRef.current.addEventListener('touchcancel', handleTouchEnd, false)
    }
    return () => {
      if (trackRef.current) {
        trackRef.current.removeEventListener('touchstart', handleTouchStart, false)
        trackRef.current.removeEventListener('touchmove', handleTouchMove, false)
        trackRef.current.removeEventListener('touchend', handleTouchEnd, false)
        trackRef.current.removeEventListener('touchcancel', handleTouchEnd, false)
      }
    }
  }, [])

  useEffect(() => {
    if (activeRef.current) {
      setTrackX(activeRef.current.offsetLeft - offsetCompensation)
    }
  }, [activeSlide, offsetCompensation, items])

  useEffect(() => {
    setDragLeft(0)
    _activeSlide = activeSlide
    if (onChange) onChange(_activeSlide)
  }, [activeSlide])

  return (
    <div className={classes}>
      <div className={styles.wrapper} style={styleOverride}>
        <div
          className={`${styles.track} ${trackClasses}`}
          ref={trackRef}
          style={{
            transform: `translateX(-${trackX}px)`,
            left: dragLeft > 0 ? `-${dragLeft}px` : `${Math.abs(dragLeft)}px`,
          }}
        >
          {items &&
            items.map((item, i) => (
              <div
                key={`slide_${i}`}
                ref={activeSlide === i ? activeRef : undefined}
                className={`${fill} ${activeSlide === i ? 'isActive' : ''} ${slideClassName}`}
                data-testid="slide"
              >
                {item}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
export default forwardRef(Slider)
